import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../components/AdminHeader';
import Badge, { StatusBadge } from '../components/ui/Badge';
import { getAdminDrivers } from '../store/thunks/driverActions';
import { useDispatch, useSelector } from 'react-redux';
import { Alert } from '@mui/material';

/**
 * Redesigned driver management screen backed by /api/v1/admin/drivers.
 * Provides quick-filter chips for the operational states we care about
 * (active, inactive, restricted, missing docs, below 3.0) and server-side
 * search + market filtering so the list scales past client-filterable sizes.
 */

const FILTER_CHIPS = [
  { id: 'all', label: 'All Drivers' },
  { id: 'active', label: 'Active' },
  { id: 'inactive', label: 'Inactive' },
  { id: 'restricted', label: 'Restricted' },
  { id: 'missing_docs', label: 'Missing Docs' },
  { id: 'below_rating', label: 'Below 3.0' },
];

const complianceVariant = {
  ready: 'success',
  pending: 'warning',
  rejected: 'danger',
  missing: 'default',
};

const complianceLabel = {
  ready: 'Ready',
  pending: 'Pending Review',
  rejected: 'Rejected',
  missing: 'Missing',
};

function RatingStar({ value }) {
  const rounded = Number(value ?? 5).toFixed(1);
  const tone = value < 3 ? 'text-red-600' : value < 4 ? 'text-amber-600' : 'text-emerald-600';
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${tone}`}>
      <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
        <path d="M10 15.27 16.18 19l-1.64-7.03L20 7.24l-7.19-.61L10 0 7.19 6.63 0 7.24l5.46 4.73L3.82 19z" />
      </svg>
      {rounded}
    </span>
  );
}

function DriverListScreen() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [market, setMarket] = useState('');

  const { adminDriversLoading, adminDrivers, adminDriversError } = useSelector(
    (state) => state.allDrivers ?? {}
  );

  useEffect(() => {
    const params = {};
    if (filter !== 'all') params.status = filter;
    if (search.trim()) params.search = search.trim();
    if (market.trim()) params.market = market.trim();
    const timer = setTimeout(() => {
      dispatch(getAdminDrivers(params));
    }, 200);
    return () => clearTimeout(timer);
  }, [dispatch, filter, search, market]);

  const drivers = useMemo(() => adminDrivers?.drivers ?? [], [adminDrivers]);

  return (
    <div className="flex w-full h-screen overflow-auto">
      {adminDriversError && (
        <Alert
          severity="error"
          sx={{ position: 'fixed', width: '90%', margin: 'auto', top: 20, left: 0, right: 0, zIndex: 10 }}
        >
          <strong>{adminDriversError?.message || 'Could not load drivers.'}</strong>
        </Alert>
      )}

      <div className="flex flex-col w-full mx-auto bg-[#F5F5F5]">
        <div className="px-10 bg-white">
          <AdminHeader heading={'Drivers'} pages={'Pages / Drivers'} />
        </div>

        <div className="px-6 py-5 h-screen overflow-auto">
          {/* Filters + search */}
          <div className="bg-white rounded-xl px-5 py-4 mb-4 shadow-sm">
            <div className="flex flex-wrap gap-2 mb-3">
              {FILTER_CHIPS.map((chip) => (
                <button
                  key={chip.id}
                  onClick={() => setFilter(chip.id)}
                  className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors border ${
                    filter === chip.id
                      ? 'bg-[#4F46E5] text-white border-[#4F46E5]'
                      : 'bg-white text-[#3F3F46] border-[#E4E4E7] hover:border-[#A5B4FC]'
                  }`}
                >
                  {chip.label}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <input
                className="flex-1 min-w-[240px] px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Search by name, email or phone"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
              <input
                className="w-48 px-3 py-2 rounded-lg border border-[#E4E4E7] text-sm focus:outline-none focus:border-[#4F46E5]"
                placeholder="Filter by market"
                value={market}
                onChange={(e) => setMarket(e.target.value)}
              />
            </div>
          </div>

          {/* Drivers table */}
          <div className="bg-white rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-[#FAFAFA] text-[#71717A] text-xs uppercase tracking-wide">
                  <tr>
                    <th className="text-left px-5 py-3 font-semibold">Driver</th>
                    <th className="text-left px-5 py-3 font-semibold">Status</th>
                    <th className="text-left px-5 py-3 font-semibold">Rating</th>
                    <th className="text-left px-5 py-3 font-semibold">Compliance</th>
                    <th className="text-left px-5 py-3 font-semibold">Market</th>
                    <th className="text-left px-5 py-3 font-semibold">Orders</th>
                    <th className="text-left px-5 py-3 font-semibold">Last Active</th>
                    <th className="text-right px-5 py-3 font-semibold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adminDriversLoading && (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-[#71717A]">
                        Loading drivers…
                      </td>
                    </tr>
                  )}
                  {!adminDriversLoading && drivers.length === 0 && (
                    <tr>
                      <td colSpan={8} className="text-center py-10 text-[#71717A]">
                        No drivers match the current filters.
                      </td>
                    </tr>
                  )}
                  {drivers.map((d) => {
                    const isSuspended = d.suspendedUntil && new Date(d.suspendedUntil) > new Date();
                    const statusLabel = isSuspended
                      ? 'suspended'
                      : d.isActive
                        ? 'active'
                        : 'inactive';
                    const statusVariant =
                      statusLabel === 'suspended' ? 'warning' : statusLabel === 'active' ? 'success' : 'default';
                    return (
                      <tr key={d._id} className="border-t border-[#F4F4F5] hover:bg-[#FAFAFA]">
                        <td className="px-5 py-4">
                          <div className="font-medium text-[#18181B]">
                            {d.firstName} {d.lastName}
                          </div>
                          <div className="text-xs text-[#71717A]">{d.email}</div>
                          <div className="text-xs text-[#71717A]">{d.phoneNumber}</div>
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant={statusVariant} dot>
                            {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
                          </Badge>
                          {d.eligibility && !d.eligibility.eligible && (
                            <div className="mt-1 text-[10px] text-[#B91C1C] uppercase tracking-wide">
                              {d.eligibility.reasons[0]?.replaceAll('_', ' ')}
                            </div>
                          )}
                        </td>
                        <td className="px-5 py-4">
                          <RatingStar value={d.rating} />
                        </td>
                        <td className="px-5 py-4">
                          <Badge variant={complianceVariant[d.compliance?.state] || 'default'}>
                            {complianceLabel[d.compliance?.state] || 'Missing'}
                          </Badge>
                        </td>
                        <td className="px-5 py-4 text-[#3F3F46]">{d.market || '—'}</td>
                        <td className="px-5 py-4 text-[#3F3F46]">{d.totalOrders || 0}</td>
                        <td className="px-5 py-4 text-[#71717A] text-xs">
                          {d.lastOrderAt ? new Date(d.lastOrderAt).toLocaleDateString() : '—'}
                        </td>
                        <td className="px-5 py-4 text-right">
                          <button
                            className="bg-[#4F46E5] text-white text-xs px-4 py-1.5 rounded-full hover:bg-[#4338CA]"
                            onClick={() => navigate(`/driverList/${d._id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DriverListScreen;
